import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ShieldCheck, ArrowRight, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import AuthBackdrop from "@/components/AuthBackdrop";

// Evidence frameworks the platform actually reports against (see the ISO 27001
// report endpoint and the compliance module). Not decoration: these are the
// standards management is answerable for, which is why this console exists.
const FRAMEWORKS = ["ISO/IEC 27001", "NIST CSF", "CIS v8", "SOC 2", "RA 10173"];

const EASE = [0.16, 1, 0.3, 1];

// Mirrors backend/authz.py's CLIENT_SCOPED_ROLES (also copied in App.js,
// PortalTickets.jsx and NewTicketDialog.jsx). COPY ONLY, for one thing: which
// page to land on. App.js's `Protected` is the actual guard and redirects
// regardless of what this constant says, so getting this list wrong sends a
// client-scoped user to "/" for one render before the fail-safe catches it,
// never further.
const CLIENT_SCOPED_ROLES = [
  "client_read_only", "client_admin", "client_administrator",
  "client_manager", "client_user", "client_contact",
];
const landingPathFor = (user) => (CLIENT_SCOPED_ROLES.includes(user?.role) ? "/portal" : "/");

export default function Login() {
  const { login, demoLogin } = useAuth();
  const nav = useNavigate();
  const reduce = useReducedMotion();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // One-click sign-in. The SERVER decides whether this list has anything in
  // it: with the deployment flag off, the buttons hidden by an administrator,
  // or the demo accounts purged, it comes back empty and nothing renders.
  const [demoAccounts, setDemoAccounts] = useState([]);
  const [demoBusy, setDemoBusy] = useState("");

  // Real semantic state, not a decorative dot: ask the backend whether the
  // control plane is reachable before anyone types a credential. /api/health is
  // the one unauthenticated route, so this works pre-login.
  const [plane, setPlane] = useState("checking"); // checking | online | unreachable
  useEffect(() => {
    let alive = true;
    api
      .get("/health", { timeout: 6000 })
      .then(() => alive && setPlane("online"))
      .catch(() => alive && setPlane("unreachable"));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    api
      .get("/auth/demo-accounts")
      .then((r) => setDemoAccounts(r.data?.enabled ? r.data.accounts || [] : []))
      .catch(() => setDemoAccounts([]));
  }, []);

  const rise = useMemo(
    () => ({
      hidden: reduce ? { opacity: 1 } : { opacity: 0, y: 14 },
      show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
    }),
    [reduce],
  );

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome to AegisOne RMM");
      nav(landingPathFor(u));
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const quickSignIn = async (addr) => {
    setDemoBusy(addr);
    try {
      const u = await demoLogin(addr);
      toast.success("Welcome to AegisOne RMM");
      nav(landingPathFor(u));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDemoBusy("");
    }
  };

  return (
    <div className="relative min-h-[100dvh] w-full overflow-hidden bg-[#09090B] text-zinc-50">
      <AuthBackdrop />

      <div className="relative z-10 grid min-h-[100dvh] grid-cols-1 lg:grid-cols-[1.15fr_minmax(430px,0.85fr)]">
        {/* Hero: identity, claim, assurance */}
        <motion.section
          initial="hidden"
          animate="show"
          variants={{ show: { transition: { staggerChildren: reduce ? 0 : 0.11, delayChildren: reduce ? 0 : 0.15 } } }}
          className="flex flex-col justify-between px-6 pb-8 pt-10 sm:px-10 lg:px-12 lg:pb-14 lg:pt-16 xl:px-16"
        >
          <motion.div variants={rise} className="flex items-center gap-3">
            <span className="relative grid h-11 w-11 place-items-center rounded-md bg-gradient-to-br from-blue-600 to-sky-400 shadow-[0_8px_28px_-10px_rgba(37,99,235,0.9)]">
              <ShieldCheck size={20} strokeWidth={2} className="text-white" />
            </span>
            <span>
              <span className="block font-display text-[17px] font-bold leading-tight tracking-tight">AegisOne RMM</span>
              <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                Managed Security Operations
              </span>
            </span>
          </motion.div>

          <div className="max-w-[44rem] py-12 lg:py-0 2xl:max-w-[52rem]">
            {/* Scale is planned against the column width, not chosen for impact:
                each line must stay one line from lg up. */}
            <h1 className="font-display text-[2.1rem] font-semibold leading-[1.15] tracking-tight sm:text-[2.6rem] lg:text-[2.05rem] xl:text-[2.7rem] min-[1400px]:text-[3.05rem] 2xl:text-[3.4rem]">
              {["Every endpoint accounted for.", "Every control evidenced."].map((line, i) => (
                <span key={line} className="-mb-2 block overflow-hidden pb-2">
                  <motion.span
                    className="block"
                    variants={{
                      hidden: reduce ? { opacity: 1 } : { y: "105%" },
                      show: { y: "0%", opacity: 1, transition: { duration: 0.9, ease: EASE } },
                    }}
                  >
                    {i === 1 ? <span className="text-zinc-400">{line}</span> : line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p variants={rise} className="mt-6 max-w-[52ch] text-[15px] leading-relaxed text-zinc-400">
              Remote monitoring, endpoint control and ISO 27001 evidence for every client you are accountable for.
            </motion.p>
          </div>

          {/* Assurance rail: its own zone on a hairline at the base of the hero,
              not stuffed into the headline block. */}
          <motion.div variants={rise} className="border-t border-zinc-800/80 pt-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">Evidence mapped to</p>
            {/* Hairline separators only where the row is guaranteed not to wrap
                (xl+). A wrapped flex row leaves a rule dangling at the start of
                the next line, which reads as a rendering fault. */}
            <ul className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2">
              {FRAMEWORKS.map((f, i) => (
                <li
                  key={f}
                  className={
                    "text-[13px] font-medium text-zinc-400 " +
                    (i > 0 ? "xl:border-l xl:border-zinc-800 xl:pl-6" : "")
                  }
                >
                  {f}
                </li>
              ))}
            </ul>
          </motion.div>
        </motion.section>

        {/* Action surface: sign-in */}
        <motion.section
          initial={reduce ? false : { opacity: 0, x: 28 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ type: "spring", stiffness: 90, damping: 20, delay: reduce ? 0 : 0.35 }}
          className="relative flex items-center justify-center border-t border-zinc-800/80 bg-zinc-950/80 px-6 py-12 backdrop-blur-xl sm:px-10 lg:border-l lg:border-t-0 lg:py-16"
        >
          {/* The seam between the claim and the action, drawn once as the page
              assembles. It states the structure rather than decorating it. */}
          <motion.span
            aria-hidden="true"
            initial={reduce ? false : { scaleY: 0 }}
            animate={{ scaleY: 1 }}
            transition={{ duration: 1.1, ease: EASE, delay: reduce ? 0 : 0.45 }}
            className="absolute left-[-1px] top-0 hidden h-full w-px origin-top bg-gradient-to-b from-transparent via-blue-500/60 to-transparent lg:block"
          />

          <motion.div
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { staggerChildren: reduce ? 0 : 0.07, delayChildren: reduce ? 0 : 0.55 } } }}
            className="w-full max-w-sm"
          >
            <motion.div variants={rise}>
              <h2 className="font-display text-2xl font-semibold tracking-tight">Operator sign-in</h2>
              <p className="mt-2 text-sm text-zinc-500">Authorised personnel of the managing service provider.</p>
            </motion.div>

            <form onSubmit={submit} className="mt-8 space-y-5" noValidate>
              <motion.div variants={rise} className="flex flex-col gap-2">
                <label htmlFor="login-email" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  Work email
                </label>
                <input
                  id="login-email"
                  data-testid="login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  autoComplete="username"
                  required
                  className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3.5 py-2.5 text-sm text-zinc-100 transition-colors duration-200 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                />
              </motion.div>

              <motion.div variants={rise} className="flex flex-col gap-2">
                <label htmlFor="login-password" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  Password
                </label>
                <input
                  id="login-password"
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  autoComplete="current-password"
                  required
                  className="w-full rounded-md border border-zinc-800 bg-zinc-900/60 px-3.5 py-2.5 text-sm text-zinc-100 transition-colors duration-200 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                />
              </motion.div>

              {/* Inline error, in the form, beside what failed. Toast stays too. */}
              <AnimatePresence initial={false}>
                {error ? (
                  <motion.p
                    key="err"
                    role="alert"
                    initial={reduce ? false : { opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.25, ease: EASE }}
                    className="flex items-start gap-2 overflow-hidden text-[13px] text-red-400"
                  >
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    <span>{error}</span>
                  </motion.p>
                ) : null}
              </AnimatePresence>

              <motion.button
                variants={rise}
                data-testid="login-submit"
                type="submit"
                disabled={loading}
                whileHover={reduce || loading ? undefined : { y: -1 }}
                whileTap={reduce || loading ? undefined : { scale: 0.985 }}
                className="group inline-flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_10px_30px_-12px_rgba(37,99,235,0.9)] transition-colors duration-200 hover:bg-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/60 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950 disabled:cursor-not-allowed disabled:opacity-70"
              >
                {loading ? (
                  <>
                    <Loader2 size={15} className="animate-spin" />
                    Verifying
                  </>
                ) : (
                  <>
                    Sign in
                    <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                  </>
                )}
              </motion.button>
            </form>

            {demoAccounts.length > 0 ? (
              <motion.div variants={rise} className="mt-7" data-testid="demo-logins">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-px flex-1 bg-zinc-800/80" />
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600 whitespace-nowrap">
                    Or sign in as
                  </span>
                  <div className="h-px flex-1 bg-zinc-800/80" />
                </div>
                <div className="rounded-md border border-zinc-800 divide-y divide-zinc-800 overflow-hidden">
                  {demoAccounts.map((a) => (
                    <button
                      key={a.email}
                      type="button"
                      data-testid={`demo-login-${a.role}`}
                      disabled={!!demoBusy}
                      onClick={() => quickSignIn(a.email)}
                      className="w-full flex items-center gap-3 px-3.5 py-2.5 text-left transition-colors duration-150 hover:bg-zinc-900/70 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-blue-400 flex-1 truncate">
                        {a.role_label}
                      </span>
                      <span className="text-[13px] text-zinc-400 truncate max-w-[9rem]">
                        {demoBusy === a.email ? "Signing in…" : a.name}
                      </span>
                      <ArrowRight size={13} className="text-zinc-600 shrink-0" />
                    </button>
                  ))}
                </div>
                <p className="font-mono text-[10px] text-zinc-600 mt-2 leading-relaxed">
                  Demonstration accounts. No password is shown or sent — the server signs you in.
                </p>
              </motion.div>
            ) : null}

            <motion.div variants={rise} className="mt-8 flex flex-col gap-4 border-t border-zinc-800/80 pt-5">
              <PlaneStatus state={plane} reduce={reduce} />
              <p className="text-[13px] text-zinc-500">
                No account?{" "}
                <Link
                  to="/register"
                  data-testid="register-link"
                  className="text-blue-400 underline-offset-4 hover:underline"
                >
                  Request access
                </Link>
              </p>
            </motion.div>
          </motion.div>
        </motion.section>
      </div>
    </div>
  );
}

/**
 * Live control-plane reachability. This dot is allowed to exist because it
 * carries real state: if the API is unreachable, an operator learns it here
 * instead of from a failed sign-in that looks like a wrong password.
 *
 * Deliberately NOT wrapped in AnimatePresence. A crossfade would make the
 * displayed state depend on an exit animation finishing, and a status readout
 * that can lag behind the thing it reports is worse than one that just snaps.
 * The transition is a plain CSS colour fade, which cannot get stuck.
 */
function PlaneStatus({ state, reduce }) {
  const map = {
    checking: { dot: "bg-zinc-600", text: "text-zinc-500", label: "Checking control plane" },
    online: { dot: "bg-emerald-400", text: "text-zinc-400", label: "Control plane online" },
    unreachable: { dot: "bg-amber-400", text: "text-amber-400", label: "Control plane unreachable" },
  };
  const s = map[state];
  return (
    <p
      aria-live="polite"
      className={
        "inline-flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-500 " +
        s.text
      }
    >
      <span className="relative grid h-2 w-2 place-items-center">
        <span className={"h-2 w-2 rounded-full transition-colors duration-500 " + s.dot} />
        {state === "online" && !reduce ? (
          <motion.span
            className="absolute inset-0 rounded-full bg-emerald-400"
            animate={{ scale: [1, 2.4], opacity: [0.55, 0] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut" }}
          />
        ) : null}
      </span>
      {s.label}
    </p>
  );
}
